import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getAllBrandPresets } from '../config/brandPresets';
import BrandPresetManager from './BrandPresetManager';
import { dimensionPresetStorage } from '../services/dimensionPresetStorage';
import toast from 'react-hot-toast';

const { FiSave, FiEye, FiMonitor, FiSmartphone, FiTablet, FiInfo, FiCheck, FiSettings, FiMaximize, FiSquare, FiPlus, FiTrash2, FiEdit2 } = FiIcons;

// Built-in dimension presets
const BUILT_IN_PRESETS = [
  {
    id: 'responsive',
    name: 'Responsive Full Screen',
    icon: FiMaximize,
    description: 'Adaptive - fills available space',
    mode: 'responsive',
    width: '100%',
    height: '100%',
    maxWidth: 980,
    aspectRatio: null,
    isBuiltIn: true
  },
  {
    id: '16-9',
    name: '16:9 (Video/Modern)',
    icon: FiMonitor,
    description: '1280×720 - YouTube, slides, web content',
    mode: 'fixed',
    width: 1280,
    height: 720,
    maxWidth: 980,
    aspectRatio: '16/9',
    isBuiltIn: true
  },
  {
    id: '4-3',
    name: '4:3 (Classic)',
    icon: FiTablet,
    description: '1024×768 - Training, older slides',
    mode: 'fixed',
    width: 1024,
    height: 768,
    maxWidth: 980,
    aspectRatio: '4/3',
    isBuiltIn: true
  },
  {
    id: '1-1',
    name: '1:1 (Square)',
    icon: FiSquare,
    description: '800×800 - Widgets, cards, tools',
    mode: 'fixed',
    width: 800,
    height: 800,
    maxWidth: 980,
    aspectRatio: '1/1',
    isBuiltIn: true
  },
  {
    id: '9-16',
    name: '9:16 (Portrait)',
    icon: FiSmartphone,
    description: '720×1280 - Mobile, reels, tall content',
    mode: 'fixed',
    width: 720,
    height: 1280,
    maxWidth: 980,
    aspectRatio: '9/16',
    isBuiltIn: true
  },
  {
    id: '4-5',
    name: '4:5 (Tall Portrait)',
    icon: FiSmartphone,
    description: '800×1000 - Blog posts, social',
    mode: 'fixed',
    width: 800,
    height: 1000,
    maxWidth: 980,
    aspectRatio: '4/5',
    isBuiltIn: true
  }
];

function EmbedBuilder({ embed, onSave, onCancel }) {
  const [brandPresets, setBrandPresets] = useState({});
  const [customDimensionPresets, setCustomDimensionPresets] = useState([]);
  const [showSavePresetDialog, setShowSavePresetDialog] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [editingPresetId, setEditingPresetId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    toolUrl: '',
    brand: 'wwsc',
    layout: {
      maxWidth: 980,
      padding: 16,
      backgroundColor: '#f8fafc',
      borderRadius: 16,
      headerText: '',
      showHeader: true,
      headerStyle: 'solid'
    },
    iframe: {
      width: '100%',
      heightMode: 'auto',
      minHeight: 700,
      fixedHeight: 800,
      dimensionPreset: 'responsive',
      customWidth: 1280,
      customHeight: 720,
      allow: 'camera; microphone; clipboard-write; fullscreen',
      sandbox: 'allow-scripts allow-same-origin allow-forms allow-popups'
    }
  });
  const [previewMode, setPreviewMode] = useState('desktop');
  const [showBrandSelector, setShowBrandSelector] = useState(false);
  const [showPresetManager, setShowPresetManager] = useState(false);

  useEffect(() => {
    loadBrandPresets();
    loadCustomDimensionPresets();
  }, []);

  useEffect(() => {
    if (embed) {
      setFormData(embed);
    }
  }, [embed]);

  const loadBrandPresets = async () => {
    const presets = await getAllBrandPresets();
    setBrandPresets(presets);
  };

  const loadCustomDimensionPresets = async () => {
    const presets = await dimensionPresetStorage.getAllPresets();
    setCustomDimensionPresets(presets);
  };

  const handleInputChange = (path, value) => {
    setFormData(prev => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleDimensionPresetClick = (preset) => {
    setFormData(prev => ({
      ...prev,
      layout: {
        ...prev.layout,
        maxWidth: preset.maxWidth || 980
      },
      iframe: {
        ...prev.iframe,
        dimensionPreset: preset.id,
        heightMode: preset.mode === 'responsive' ? 'auto' : 'fixed',
        fixedHeight: preset.height === '100%' ? 800 : preset.height,
        customWidth: preset.width === '100%' ? 1280 : preset.width,
        customHeight: preset.height === '100%' ? 720 : preset.height
      }
    }));
  };

  const handleSaveCurrentAsPreset = () => {
    setShowSavePresetDialog(true);
    setPresetName('');
    setEditingPresetId(null);
  };

  const handleSaveDimensionPreset = async () => {
    if (!presetName.trim()) {
      toast.error('Please enter a preset name');
      return;
    }

    try {
      const presetData = {
        name: presetName,
        description: `${formData.iframe.customWidth}×${formData.iframe.customHeight} @ ${formData.layout.maxWidth}px max width`,
        mode: 'fixed',
        width: formData.iframe.customWidth,
        height: formData.iframe.customHeight,
        maxWidth: formData.layout.maxWidth,
        aspectRatio: `${formData.iframe.customWidth}/${formData.iframe.customHeight}`,
        icon: FiSettings,
        isBuiltIn: false
      };

      if (editingPresetId) {
        await dimensionPresetStorage.updatePreset(editingPresetId, presetData);
        toast.success('Dimension preset updated successfully');
      } else {
        await dimensionPresetStorage.createPreset(presetData);
        toast.success('Dimension preset saved successfully');
      }

      await loadCustomDimensionPresets();
      setShowSavePresetDialog(false);
      setPresetName('');
      setEditingPresetId(null);
    } catch (error) {
      toast.error('Failed to save dimension preset');
    }
  };

  const handleEditCustomPreset = (preset) => {
    setEditingPresetId(preset.id);
    setPresetName(preset.name);
    setShowSavePresetDialog(true);
  };

  const handleDeleteCustomPreset = async (presetId) => {
    if (window.confirm('Are you sure you want to delete this dimension preset?')) {
      try {
        await dimensionPresetStorage.deletePreset(presetId);
        await loadCustomDimensionPresets();
        toast.success('Dimension preset deleted successfully');
      } catch (error) {
        toast.error('Failed to delete dimension preset');
      }
    }
  };

  const handleBrandChange = (brandKey) => {
    const preset = brandPresets[brandKey];
    if (preset) {
      setFormData(prev => ({
        ...prev,
        brand: brandKey,
        layout: {
          ...prev.layout,
          backgroundColor: preset.backgroundColor,
          borderRadius: preset.layout.borderRadius,
          showHeader: preset.layout.showHeader,
          headerStyle: preset.layout.headerStyle
        }
      }));
      setShowBrandSelector(false);
    }
  };

  const currentBrand = brandPresets[formData.brand] || brandPresets.wwsc || {};

  // Combine built-in and custom presets
  const allDimensionPresets = [...BUILT_IN_PRESETS, ...customDimensionPresets];
  const selectedPreset = allDimensionPresets.find(p => p.id === formData.iframe.dimensionPreset) || BUILT_IN_PRESETS[0];
  const isCustomDimensions = !allDimensionPresets.find(p => p.id === formData.iframe.dimensionPreset);

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Configuration Form */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-6 max-h-[80vh] overflow-y-auto pr-2 custom-scrollbar"
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSave(formData);
            }}
            className="space-y-6"
          >
            {/* Section: Core Config */}
            <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
              <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900 mb-4">
                <SafeIcon icon={FiInfo} className="text-blue-500" />
                Core Configuration
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Tool Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => {
                      handleInputChange('title', e.target.value);
                      if (!embed) {
                        const slug = e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                        handleInputChange('slug', slug);
                      }
                    }}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                    placeholder="e.g. Rhythm Checklist"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">URL Slug</label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => handleInputChange('slug', e.target.value)}
                      className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl font-mono text-sm"
                      placeholder="rhythm-checklist"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Brand Wrapper</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowBrandSelector(!showBrandSelector)}
                        className="flex-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl outline-none hover:border-blue-400 transition-all flex items-center justify-between"
                      >
                        <span className="text-sm font-medium truncate">{currentBrand.name || 'Select Brand'}</span>
                        <div
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: currentBrand.primaryColor || '#2563eb' }}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowPresetManager(true)}
                        className="px-3 py-2 bg-purple-100 text-purple-600 rounded-xl hover:bg-purple-200 transition-colors"
                        title="Manage Presets"
                      >
                        <SafeIcon icon={FiSettings} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Brand Selector Modal */}
                <AnimatePresence>
                  {showBrandSelector && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
                      onClick={() => setShowBrandSelector(false)}
                    >
                      <motion.div
                        initial={{ y: 20 }}
                        animate={{ y: 0 }}
                        className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto p-8"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-between mb-6">
                          <h2 className="text-2xl font-bold text-gray-900">Choose Your Brand Wrapper</h2>
                          <button
                            type="button"
                            onClick={() => setShowPresetManager(true)}
                            className="bg-purple-100 text-purple-600 px-4 py-2 rounded-xl font-medium hover:bg-purple-200 transition-colors flex items-center gap-2"
                          >
                            <SafeIcon icon={FiSettings} />
                            Manage Presets
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {Object.entries(brandPresets).map(([key, brand]) => (
                            <motion.button
                              key={key}
                              type="button"
                              whileHover={{ scale: 1.05, y: -5 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => handleBrandChange(key)}
                              className={`relative bg-white border-2 rounded-2xl p-6 text-left transition-all overflow-hidden group ${
                                formData.brand === key
                                  ? 'border-blue-500 shadow-xl shadow-blue-100'
                                  : 'border-gray-200 hover:border-gray-300 hover:shadow-lg'
                              }`}
                            >
                              {formData.brand === key && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className="absolute top-3 right-3 bg-blue-500 text-white rounded-full p-1"
                                >
                                  <SafeIcon icon={FiCheck} className="text-sm" />
                                </motion.div>
                              )}

                              {brand.isDefault && (
                                <div className="absolute top-3 left-3 bg-gray-100 text-gray-600 px-2 py-1 rounded-lg text-xs font-bold">
                                  Default
                                </div>
                              )}

                              {brand.isCustom && (
                                <div className="absolute top-3 left-3 bg-purple-100 text-purple-600 px-2 py-1 rounded-lg text-xs font-bold">
                                  Custom
                                </div>
                              )}

                              <div
                                className="h-20 rounded-xl mb-4 flex items-center justify-center relative overflow-hidden"
                                style={{
                                  background:
                                    brand.layout.headerStyle === 'gradient'
                                      ? `linear-gradient(to right, ${brand.primaryColor}, ${brand.secondaryColor})`
                                      : brand.layout.headerStyle === 'minimal'
                                      ? brand.backgroundColor
                                      : brand.primaryColor
                                }}
                              >
                                {brand.logoUrl && (
                                  <img src={brand.logoUrl} alt={brand.name} className="h-10 w-auto object-contain" />
                                )}
                                {!brand.logoUrl && (
                                  <div className="text-lg font-bold" style={{ color: brand.textColor }}>
                                    {brand.name}
                                  </div>
                                )}
                              </div>

                              <h3 className="font-bold text-gray-900 mb-2">{brand.name}</h3>
                              <div className="space-y-1 text-xs text-gray-500">
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: brand.primaryColor }} />
                                  <span>Primary Color</span>
                                </div>
                                <div>
                                  Style: <span className="font-medium capitalize">{brand.layout.headerStyle}</span>
                                </div>
                                <div>
                                  Radius: <span className="font-medium">{brand.layout.borderRadius}px</span>
                                </div>
                              </div>
                            </motion.button>
                          ))}
                        </div>

                        <div className="mt-6 flex justify-end">
                          <button
                            type="button"
                            onClick={() => setShowBrandSelector(false)}
                            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                          >
                            Close
                          </button>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Source Tool URL</label>
                  <input
                    type="url"
                    value={formData.toolUrl}
                    onChange={(e) => handleInputChange('toolUrl', e.target.value)}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                    placeholder="https://netlify-app.com/quiz"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Link Preview Title</label>
                  <input
                    type="text"
                    value={formData.layout.ogTitle || ''}
                    onChange={(e) => handleInputChange('layout.ogTitle', e.target.value)}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 transition-all outline-none"
                    placeholder={formData.title || 'e.g. [WWSC] Street Outreach Course - Lesson 19'}
                  />
                </div>
              </div>
            </div>

            {/* Section: Look & Feel */}
            <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Wrapper Design</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Header Title (On Wrapper)</label>
                  <input
                    type="text"
                    value={formData.layout.headerText}
                    onChange={(e) => handleInputChange('layout.headerText', e.target.value)}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                    placeholder="Leave blank to use Tool Title"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Max Width (px)</label>
                    <input
                      type="number"
                      value={formData.layout.maxWidth}
                      onChange={(e) => handleInputChange('layout.maxWidth', parseInt(e.target.value))}
                      className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Border Radius (px)</label>
                    <input
                      type="number"
                      value={formData.layout.borderRadius}
                      onChange={(e) => handleInputChange('layout.borderRadius', parseInt(e.target.value))}
                      className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Header Style</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['solid', 'gradient', 'minimal'].map((style) => (
                      <button
                        key={style}
                        type="button"
                        onClick={() => handleInputChange('layout.headerStyle', style)}
                        className={`py-2 px-3 rounded-lg text-xs font-medium border-2 transition-all capitalize ${
                          formData.layout.headerStyle === style
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {style}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-gray-700">Show Header</label>
                  <button
                    type="button"
                    onClick={() => handleInputChange('layout.showHeader', !formData.layout.showHeader)}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      formData.layout.showHeader ? 'bg-blue-500' : 'bg-gray-300'
                    }`}
                  >
                    <motion.div
                      animate={{ x: formData.layout.showHeader ? 28 : 2 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      className="absolute top-1 w-5 h-5 bg-white rounded-full shadow-md"
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Section: Dimension Presets */}
            <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Embed Dimensions</h3>
                <button
                  type="button"
                  onClick={handleSaveCurrentAsPreset}
                  className="bg-green-100 text-green-700 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-green-200 transition-colors flex items-center gap-1"
                >
                  <SafeIcon icon={FiPlus} className="text-xs" />
                  Save Current as Preset
                </button>
              </div>

              <div className="space-y-2 mb-4 max-h-[400px] overflow-y-auto pr-2">
                {/* Built-in Presets */}
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Built-in Presets</p>
                  {BUILT_IN_PRESETS.map((preset) => (
                    <motion.button
                      key={preset.id}
                      type="button"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={() => handleDimensionPresetClick(preset)}
                      className={`w-full p-3 rounded-lg border-2 transition-all text-left mb-2 ${
                        formData.iframe.dimensionPreset === preset.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div
                          className={`p-1.5 rounded-lg flex-shrink-0 ${
                            formData.iframe.dimensionPreset === preset.id
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          <SafeIcon icon={preset.icon} className="text-sm" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <h4 className="font-semibold text-sm text-gray-900 truncate">{preset.name}</h4>
                            {formData.iframe.dimensionPreset === preset.id && (
                              <SafeIcon icon={FiCheck} className="text-blue-500 flex-shrink-0 ml-2" />
                            )}
                          </div>
                          <p className="text-xs text-gray-600 truncate">{preset.description}</p>
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>

                {/* Custom Presets */}
                {customDimensionPresets.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Your Custom Presets</p>
                    {customDimensionPresets.map((preset) => (
                      <motion.div
                        key={preset.id}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`w-full p-3 rounded-lg border-2 transition-all mb-2 ${
                          formData.iframe.dimensionPreset === preset.id
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <button
                            type="button"
                            onClick={() => handleDimensionPresetClick(preset)}
                            className="flex-1 flex items-start gap-2 text-left"
                          >
                            <div
                              className={`p-1.5 rounded-lg flex-shrink-0 ${
                                formData.iframe.dimensionPreset === preset.id
                                  ? 'bg-purple-500 text-white'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              <SafeIcon icon={FiSettings} className="text-sm" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-0.5">
                                <h4 className="font-semibold text-sm text-gray-900 truncate">{preset.name}</h4>
                                {formData.iframe.dimensionPreset === preset.id && (
                                  <SafeIcon icon={FiCheck} className="text-purple-500 flex-shrink-0 ml-2" />
                                )}
                              </div>
                              <p className="text-xs text-gray-600 truncate">{preset.description}</p>
                            </div>
                          </button>
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => handleEditCustomPreset(preset)}
                              className="p-1.5 text-gray-500 hover:text-blue-600 transition-colors"
                              title="Edit preset"
                            >
                              <SafeIcon icon={FiEdit2} className="text-xs" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteCustomPreset(preset.id)}
                              className="p-1.5 text-gray-500 hover:text-red-600 transition-colors"
                              title="Delete preset"
                            >
                              <SafeIcon icon={FiTrash2} className="text-xs" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Manual Dimension Inputs (Always Visible) */}
              <div className="p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-3">Manual Dimensions (Tweak to Perfection)</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Width (px)</label>
                    <input
                      type="number"
                      value={formData.iframe.customWidth}
                      onChange={(e) => {
                        const newWidth = parseInt(e.target.value);
                        handleInputChange('iframe.customWidth', newWidth);
                        handleInputChange('iframe.dimensionPreset', 'custom-manual');
                        handleInputChange('iframe.heightMode', 'fixed');
                      }}
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm"
                      min="100"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Height (px)</label>
                    <input
                      type="number"
                      value={formData.iframe.customHeight}
                      onChange={(e) => {
                        const newHeight = parseInt(e.target.value);
                        handleInputChange('iframe.customHeight', newHeight);
                        handleInputChange('iframe.fixedHeight', newHeight);
                        handleInputChange('iframe.dimensionPreset', 'custom-manual');
                        handleInputChange('iframe.heightMode', 'fixed');
                      }}
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm"
                      min="100"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-800">
                  <strong>Current:</strong> {formData.iframe.customWidth}×{formData.iframe.customHeight} @ {formData.layout.maxWidth}px max width
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  All dimensions are mobile-safe and will scale down to fit smaller screens while preserving aspect ratio.
                </p>
              </div>
            </div>

            <div className="flex gap-4 pt-4 sticky bottom-0 bg-white/80 backdrop-blur-sm py-4">
              <button
                type="submit"
                className="flex-1 bg-blue-600 text-white font-bold py-3 rounded-2xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiSave} />
                Save Embed URL
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-3 bg-gray-100 text-gray-600 font-bold rounded-2xl hover:bg-gray-200 transition-all"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>

        {/* Live Preview Panel */}
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="sticky top-0">
          <div className="bg-gray-900 rounded-3xl p-4 shadow-2xl border-8 border-gray-800">
            <div className="flex items-center justify-between mb-4 px-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <div className="flex bg-gray-800 rounded-lg p-1">
                {[
                  { id: 'mobile', icon: FiSmartphone },
                  { id: 'tablet', icon: FiTablet },
                  { id: 'desktop', icon: FiMonitor }
                ].map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => setPreviewMode(mode.id)}
                    className={`p-2 rounded-md transition-all ${
                      previewMode === mode.id ? 'bg-gray-700 text-white shadow-sm' : 'text-gray-500 hover:text-gray-300'
                    }`}
                  >
                    <SafeIcon icon={mode.icon} />
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-gray-800 rounded-2xl overflow-hidden aspect-[4/3] flex items-center justify-center p-4">
              <div
                className={`transition-all duration-500 ease-in-out shadow-2xl overflow-hidden ${
                  previewMode === 'mobile' ? 'w-[320px]' : previewMode === 'tablet' ? 'w-[480px]' : 'w-full'
                }`}
                style={{
                  backgroundColor: formData.layout.backgroundColor,
                  borderRadius: `${formData.layout.borderRadius}px`,
                  height: '100%',
                  maxHeight: '100%'
                }}
              >
                {formData.layout.showHeader && (
                  <div
                    className="px-4 py-3 flex items-center gap-3"
                    style={{
                      background:
                        formData.layout.headerStyle === 'gradient'
                          ? `linear-gradient(to right, ${currentBrand.primaryColor}, ${currentBrand.secondaryColor})`
                          : formData.layout.headerStyle === 'minimal'
                          ? 'transparent'
                          : currentBrand.primaryColor,
                      color: formData.layout.headerStyle === 'minimal' ? '#374151' : currentBrand.textColor,
                      borderRadius: `${formData.layout.borderRadius}px ${formData.layout.borderRadius}px 0 0`
                    }}
                  >
                    {currentBrand.logoUrl && (
                      <img src={currentBrand.logoUrl} alt="Logo" className="h-6 w-auto object-contain rounded" />
                    )}
                    <div className="font-bold truncate text-sm">
                      {formData.layout.headerText || formData.title || 'Tool Preview'}
                    </div>
                  </div>
                )}

                <div className="p-4 h-full">
                  <div className="w-full h-full bg-white/50 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-center p-4">
                    <SafeIcon icon={FiEye} className="text-3xl text-gray-400 mb-2" />
                    <div className="text-gray-500 font-medium text-xs">
                      {formData.toolUrl ? 'Tool content will appear here' : 'Enter a source URL'}
                    </div>
                    {formData.toolUrl && (
                      <div className="text-[10px] text-gray-400 mt-1 truncate max-w-full">{formData.toolUrl}</div>
                    )}
                    <div className="text-[10px] text-gray-500 mt-2 font-mono">
                      {formData.iframe.customWidth}×{formData.iframe.customHeight}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 text-center">
              <p className="text-gray-500 text-xs font-mono">
                Resulting URL: <span className="text-blue-400">{(currentBrand.domain || window.location.origin).replace(/^https?:\/\//, '')}/{formData.slug || '...'}</span>
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Save Dimension Preset Dialog */}
      <AnimatePresence>
        {showSavePresetDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => setShowSavePresetDialog(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {editingPresetId ? 'Edit Dimension Preset' : 'Save as Dimension Preset'}
              </h3>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-r-lg mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Current Settings:</strong>
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  Dimensions: {formData.iframe.customWidth}×{formData.iframe.customHeight}
                </p>
                <p className="text-xs text-blue-700">Max Width: {formData.layout.maxWidth}px</p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Preset Name</label>
                <input
                  type="text"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 transition-all outline-none"
                  placeholder="e.g. Quiz Format, Blog Post, etc."
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleSaveDimensionPreset}
                  className="flex-1 bg-green-600 text-white font-bold py-2 rounded-xl hover:bg-green-700 transition-all"
                >
                  {editingPresetId ? 'Update' : 'Save Preset'}
                </button>
                <button
                  onClick={() => {
                    setShowSavePresetDialog(false);
                    setPresetName('');
                    setEditingPresetId(null);
                  }}
                  className="px-6 py-2 bg-gray-100 text-gray-600 font-bold rounded-xl hover:bg-gray-200 transition-all"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BrandPresetManager
        isOpen={showPresetManager}
        onClose={() => setShowPresetManager(false)}
        onPresetCreated={loadBrandPresets}
      />
    </>
  );
}

export default EmbedBuilder;